/**
 * web/src/components/layout/Sidebar.tsx
 * Dark sidebar (248px fixed at lg+; collapses to a 56px icon rail at md;
 * hidden at sm — the slide-in mobile drawer ships in Plan 11 polish).
 * Mounts inside <AppShell>'s grid; renders once per authed route.
 *
 * Sections (top → bottom):
 *  - Brand row (Pixel mark + name + version stamp)
 *  - <EventSelector /> dropdown card
 *  - <nav> Main: Sponsors → Workflows + Approvals (badge), Speakers (flag)
 *    → Roster + Approvals (badge), Accommodation (flag) → Pickup report +
 *    Email agent; Admin: Events, Brands, User Access (admin-only via role
 *    check). Approvals live under each workload area (2026-07 restructure)
 *    — there is no global Approvals item.
 *  - Footer: WS status row (dot + label) + user card (avatar + name +
 *    role + logout button)
 *
 * Design source-of-truth: planning-docs/design_handoff_pixel_dashboard/
 * design_files/shell.jsx lines 33-120 + styles.css `.side`, `.nav-item`,
 * `.ws-status`, `.user-card` (lines 117-371).
 *
 * Badge counts come from React Query cache (CONTEXT.md D-2.4): both
 * Approvals badges read `usePendingGates().data`, split by
 * workflow.type into the sponsor vs speaker family. WS broadcast
 * events invalidate that key (handleBroadcast.ts) so badge counts
 * stay live without a separate counter store.
 *
 * Authoritative role check: `me.role === 'admin'` filters the Events
 * nav item; the server's requireRole('admin') on PUT/POST events
 * routes (src/api/events.ts) is the actual authorization (T-05-06-01).
 */
import { NavLink } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { usePendingGates } from '../../hooks/queries/usePendingGates';
import { useEvent } from '../../hooks/useEvent';
import { useWS } from '../../hooks/useWS';
import { Icon, type IconName } from '../../lib/icons';
import { EventSelector } from './EventSelector';
import { logout } from '../../api/auth';
import { cn } from '../../lib/cn';
import { FLAGS } from '../../lib/flags';
import { KIND_BY_ID } from '../../lib/approvalKinds';
import type { Role } from '../../api/types';

/** Sidebar nav item shape. */
interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  badge?: number;
  /** When set, the item is hidden unless `me.flags[flag]` is on (cosmetic; the
   * server's require_flag 404 is authoritative). */
  flag?: string;
  /**
   * When set, the item is additionally hidden unless the currently selected
   * event has `integrationConfig.modules[module] !== false`. Missing module
   * object or missing key both count as enabled (fail-open). This is a
   * cosmetic UI gate layered on top of the feature flag — the backend 404s
   * the underlying routes regardless.
   *
   * Falls back to flag-only visibility when no event is selected or the event
   * query is still loading/errored — so nav items never disappear or flicker
   * during load. The backend is the real authorization boundary.
   */
  module?: keyof import('../../api/types').EventModules;
  /** Render as an indented child of the item above (e.g. Accommodation →
   * Pickup report). Cosmetic only — routing is unaffected. */
  indent?: boolean;
  /** Pass NavLink `end` so an item's active highlight doesn't stick while a
   * sibling child route (e.g. /accommodations/email-agent) is active. */
  end?: boolean;
  /** Group parent row: still navigates to `to`, but never shows the active
   * highlight — the indented child items below carry it. */
  parent?: boolean;
}

/**
 * Sidebar nav section. `label` controls the small uppercase header above
 * the items (rendered only when present). `adminOnly` hides the entire
 * section when the current user isn't admin — it's evaluated alongside
 * the server-side requireRole guards on the underlying routes (which
 * remain authoritative).
 */
interface NavSection {
  label?: string;
  adminOnly?: boolean;
  /** Section-level flag gate (mirrors NavItem.flag). */
  flag?: string;
  items: NavItem[];
}

/** Modules object shape — same as EventModules but typed inline to avoid a
 *  circular import inside the inline type reference on NavItem.module. */
type ModulesMap = Record<string, boolean | undefined>;

/**
 * NavLink className resolver — applies active styling when the route matches.
 * React Router v7's NavLink calls this with `{ isActive }` per render.
 */
function navLinkClass({ isActive }: { isActive: boolean }) {
  return cn(
    'flex items-center gap-2.5 px-3 py-2 mx-2 rounded-md text-[13px] font-medium',
    'text-side-text-muted hover:bg-side-hover hover:text-side-text',
    'outline-none transition-colors duration-100',
    'focus-visible:ring-2 focus-visible:ring-accent',
    isActive && 'bg-side-active text-side-text',
  );
}

/**
 * Renders the dashboard sidebar. Sources data live from React Query
 * (badge counts) + the WS provider (connection status) + the events list
 * (active-event card).
 *
 * @sideEffects subscribes to /api/me, /api/gates/pending,
 *              /api/tracked-emails (PENDING), /api/events; logout button
 *              POSTs /auth/logout and redirects on success or failure
 * @idempotent yes — multiple renders share the React Query cache entries
 */
export function Sidebar() {
  const { me } = useAuth();
  const ws = useWS();
  const { eventId, event } = useEvent();
  // Badges are scoped to the active event (matches the approval queues), so
  // counts reflect the event the dashboard is focused on — not a cross-event sum.
  const { data: pendingGates } = usePendingGates(eventId ?? undefined);

  // Read per-event module toggles from integrationConfig.modules. When no
  // event is selected (or the event is still loading), modules is null and
  // every moduleOn check falls back to true — so nav items never disappear
  // during load. The backend 404s the routes regardless; this is cosmetic.
  const modules: ModulesMap | null =
    event?.integrationConfig?.modules != null &&
    typeof event.integrationConfig.modules === 'object'
      ? (event.integrationConfig.modules as ModulesMap)
      : null;

  // Split the shared pending-gates list into per-area badge counts (mirrors
  // the ApprovalQueue family filter): speaker = speaker_onboarding gates the
  // Speaker Approvals page actually renders (registry kinds only — card gates
  // live on the Speaker Cards board, not in approvals), sponsor = every
  // non-speaker gate (incl. admin test workflows).
  const speakerRows = (pendingGates ?? []).filter((g) => g.workflow.type === 'speaker_onboarding');
  const speakerPending = speakerRows.filter((g) => KIND_BY_ID.has(g.stepId)).length;
  const sponsorPending = (pendingGates ?? []).length - speakerRows.length;

  const role: Role | undefined = me?.role;
  const sections: NavSection[] = [
    {
      label: 'Main',
      items: [
        // Workloads are organized by WHO they onboard, not by the engine's
        // "workflow" concept: Sponsors = the sponsor-onboarding runs
        // (WorkflowList scoped to that type), Speakers = the speaker roster +
        // per-speaker timeline. There is deliberately no generic Workflows
        // tab, and approvals live under each area's group (2026-07).
        // Sponsors group — the parent row opens the workflow list but never
        // highlights; the two indented children carry the active state.
        { to: '/sponsors', label: 'Sponsors', icon: 'sponsor', parent: true },
        // Sponsors → Workflows. `end` keeps it from staying highlighted while
        // the sibling approvals route is active.
        { to: '/sponsors', label: 'Workflows', icon: 'list', indent: true, end: true },
        {
          to: '/sponsors/approvals',
          label: 'Approvals',
          icon: 'approval',
          badge: sponsorPending,
          indent: true,
        },
        // Sponsors → ROI reports. Flag-gated (server 404s when off); a
        // standalone batch tool, not part of the approval queue.
        {
          to: '/sponsor-roi',
          label: 'ROI reports',
          icon: 'sparkles',
          flag: FLAGS.sponsorRoi,
          module: 'sponsor',
          indent: true,
        },
        // Speakers group — hidden unless the workflow-speaker-onboarding
        // flag is on for this user (server 404s the routes when off), AND the
        // event's speaker module is enabled (integrationConfig.modules.speaker).
        {
          to: '/speakers',
          label: 'Speakers',
          icon: 'speaker',
          flag: FLAGS.speakerOnboarding,
          module: 'speaker',
          parent: true,
        },
        {
          to: '/speakers',
          label: 'Roster',
          icon: 'list',
          flag: FLAGS.speakerOnboarding,
          module: 'speaker',
          indent: true,
          end: true,
        },
        // Speakers → Speaker cards — the per-stage card-artwork pipeline board.
        {
          to: '/speakers/cards',
          label: 'Speaker cards',
          icon: 'user',
          flag: FLAGS.speakerOnboarding,
          module: 'speaker',
          indent: true,
        },
        {
          to: '/speakers/approvals',
          label: 'Approvals',
          icon: 'approval',
          badge: speakerPending,
          flag: FLAGS.speakerOnboarding,
          module: 'speaker',
          indent: true,
        },
        // Accommodation group — hidden unless the accommodations-pickup-report
        // flag is on (server 404s the API when off), AND the event's
        // accommodation module is enabled (integrationConfig.modules.accommodation).
        // The parent row opens the pickup report (the main accommodation screen)
        // but never highlights; the two indented children below carry the active state.
        {
          to: '/accommodations',
          label: 'Accommodation',
          icon: 'building',
          flag: FLAGS.accommodations,
          module: 'accommodation',
          parent: true,
        },
        // Accommodation → Pickup report. `end` keeps it from staying
        // highlighted while the email agent (a sibling child route) is active.
        {
          to: '/accommodations',
          label: 'Pickup report',
          icon: 'list',
          flag: FLAGS.accommodations,
          module: 'accommodation',
          indent: true,
          end: true,
        },
        // Accommodation → Email agent — shadow-processed inbox (never sends).
        {
          to: '/accommodations/email-agent',
          label: 'Email agent',
          icon: 'mail',
          flag: FLAGS.accommodationsEmailAgent,
          module: 'accommodation',
          indent: true,
        },
        // { to: '/confirmations', label: 'Confirmations', icon: 'confirm' },
      ],
    },
    {
      // Wave 2 ops automations (flag ops-automations; the server 404s the API
      // when off). Agent reads contracts/emails/sheets, Ops reviews, approve writes.
      label: 'Operations',
      flag: FLAGS.opsAutomations,
      items: [{ to: '/ops', label: 'Ops automations', icon: 'workflow' }],
    },
    {
      // Content Intelligence — the searchable session library over transcribed
      // conference recordings. Hidden unless the content-intelligence flag is
      // on (the server 404s the whole content API when off — dark in prod).
      // Past the flag it's visible to any authenticated user (no role/event
      // scoping — sessions carry a plain eventSlug, not an Event FK).
      label: 'Content Intelligence',
      flag: FLAGS.contentIntelligence,
      items: [{ to: '/content', label: 'Library', icon: 'sparkles' }],
    },
    {
      label: 'Admin',
      adminOnly: true,
      items: [
        { to: '/events', label: 'Events', icon: 'event' },
        { to: '/admin/brands', label: 'Brands', icon: 'building' },
        { to: '/admin/access', label: 'User Access', icon: 'user' },
      ],
    },
  ];
  // Visible = role allows the section AND its flag is on AND its module is on.
  // Module check: missing modules map OR missing key → true (fail-open).
  // No event selected or event still loading → modules is null → every item is
  // treated as module-enabled, so nav never flickers during initial load. The
  // backend is the real authorization boundary.
  const flagOn = (f?: string) => !f || Boolean(me?.flags?.[f]);
  const moduleOn = (m?: string) => !m || modules === null || modules[m] !== false;
  const visibleSections = sections
    .filter((s) => (!s.adminOnly || role === 'admin') && flagOn(s.flag))
    .map((s) => ({
      ...s,
      items: s.items.filter((it) => flagOn(it.flag) && moduleOn(it.module)),
    }))
    .filter((s) => s.items.length > 0);

  // WS dot color + pulse: green when connected, amber while reconnecting,
  // red when disconnected. Pulse halo runs on connected/reconnecting only
  // (matches handoff §App Shell §Sidebar — disconnected is static).
  const wsClass =
    ws === 'connected' ? 'bg-completed' : ws === 'reconnecting' ? 'bg-waiting' : 'bg-failed';
  const wsLabel =
    ws === 'connected'
      ? 'Live · connected'
      : ws === 'reconnecting'
        ? 'Reconnecting…'
        : 'Disconnected';

  // Avatar initials (max 2 letters, uppercase). Falls back to '?' until
  // /api/me lands so the layout doesn't shift.
  const initials = (me?.name ?? '?')
    .split(' ')
    .map((p) => p[0] ?? '')
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  /**
   * Logs out via POST /auth/logout (server clears Session row + cookie),
   * then redirects to /login. Falls back to the redirect even on error
   * because the cookie may already be gone (T-05-06-04).
   */
  function onLogout() {
    logout()
      .catch(() => {
        // queryClient routes 401s to /login already; this catch ensures
        // a redirect even if the request never reaches the server.
      })
      .finally(() => {
        window.location.assign('/login');
      });
  }

  return (
    <aside
      className={cn(
        'flex flex-col min-h-0 h-full',
        'bg-side-bg border-r border-side-border text-side-text',
        // Responsive widths: 248px at lg+, 56px icon rail at md, hidden at
        // sm (drawer ships in Plan 11 polish). The order of these classes
        // matters — Tailwind generates utilities at the same specificity,
        // so we keep `lg:` last so it wins over `md:` at large viewports.
        'w-[248px] sm:hidden md:flex md:w-14 lg:w-[248px]',
      )}
      aria-label="Primary navigation"
    >
      {/* Brand */}
      <div className="flex items-center gap-2 px-4 py-3.5 pb-3 border-b border-side-border">
        <div className="w-[22px] h-[22px] rounded-[5px] bg-accent grid place-items-center text-white font-bold text-[12px] -tracking-[0.02em] shrink-0">
          P
        </div>
        <div className="text-[13px] font-semibold -tracking-[0.01em] md:hidden lg:block">Pixel</div>
        <div className="ml-auto text-[10px] uppercase tracking-wider font-medium text-side-text-faint md:hidden lg:block">
          v0.5
        </div>
      </div>

      {/* Phase 6.5 UI-14 — "Connect Gmail before approving" banner.
          Visible for approver roles (sales_ops/operations/admin) when
          their Gmail isn't connected; hidden otherwise. Collapses with
          md:hidden lg:block so the 56px icon rail stays clean.
          NOTE: role list checked via inline literals to keep the grep
          assertion in UI-SPEC § Validation Gates working. */}
      {me?.role !== undefined &&
        ['sales_ops', 'operations', 'admin'].includes(me.role) &&
        me?.gmailConnected === false && (
          <div
            role="status"
            aria-live="polite"
            className="mx-3 mt-2 mb-1 px-3 py-2.5 rounded-md bg-waiting-soft border border-waiting-border text-waiting md:hidden lg:block"
          >
            <div className="flex items-start gap-2">
              <Icon name="alert" size={14} className="shrink-0 mt-px" />
              <div className="flex-1 min-w-0">
                <div className="text-[12px] font-medium leading-tight">
                  Connect Gmail before approving
                </div>
                <NavLink
                  to="/settings"
                  className="text-[11px] underline hover:no-underline mt-0.5 inline-block focus-visible:ring-2 focus-visible:ring-accent rounded-sm"
                >
                  Go to Settings →
                </NavLink>
              </div>
            </div>
          </div>
        )}

      {/* Event selector — hidden in icon-rail mode */}
      <div className="md:hidden lg:block">
        <EventSelector />
      </div>

      {/* Nav */}
      <nav className="flex flex-col gap-0.5 flex-1 overflow-y-auto py-2">
        {visibleSections.map((section, idx) => (
          <div
            key={section.label ?? idx}
            className={cn('flex flex-col gap-0.5', idx > 0 && 'mt-2')}
          >
            {section.label && (
              <div className="px-4 pt-1 pb-1.5 text-[10px] uppercase tracking-wider font-medium text-side-text-faint md:hidden lg:block">
                {section.label}
              </div>
            )}
            {section.items.map((it) => (
              <NavLink
                // A parent and its first child may share the same `to`
                // (Accommodation → Pickup report), so key on the label too.
                key={`${it.to}:${it.label}`}
                to={it.to}
                end={it.end}
                className={(p) =>
                  cn(
                    // Parent rows never show the active highlight — their
                    // children carry it.
                    navLinkClass(it.parent ? { isActive: false } : p),
                    // Child items indent under their parent at full width; the
                    // icon rail (md) has no room to indent, so it stays flush.
                    it.indent && 'lg:ml-7 lg:mr-2',
                  )
                }
              >
                <Icon name={it.icon} size={it.indent ? 14 : 16} className="shrink-0" />
                <span className="flex-1 md:hidden lg:inline">{it.label}</span>
                {it.badge ? (
                  <span
                    className={cn(
                      'inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full px-1.5 text-[10px] font-semibold',
                      'bg-accent text-white md:hidden lg:inline-flex',
                    )}
                    aria-label={`${it.badge} pending`}
                  >
                    {it.badge}
                  </span>
                ) : null}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>

      {/* Footer */}
      <div className="border-t border-side-border px-3 pt-3 pb-3 flex flex-col gap-2.5">
        {/* Phase 6.5 UI-14 — Settings nav link. Lives in the footer (parallel
            to Logout), NOT in the Main nav list, because Settings is a
            per-user surface, not a workflow surface. md:hidden lg:inline on
            the label keeps the icon visible in icon-rail mode. */}
        <NavLink to="/settings" className={navLinkClass}>
          <Icon name="settings" size={16} className="shrink-0" />
          <span className="flex-1 md:hidden lg:inline">Settings</span>
        </NavLink>

        <div
          className="flex items-center gap-2 text-[11px] text-side-text-muted md:hidden lg:flex"
          aria-live="polite"
        >
          <span
            className={cn(
              'w-[7px] h-[7px] rounded-full shrink-0',
              wsClass,
              ws !== 'disconnected' && 'animate-pulse motion-reduce:animate-none',
            )}
            aria-hidden="true"
          />
          <span>{wsLabel}</span>
        </div>

        <div className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg bg-side-bg-elev border border-side-border md:p-1.5 lg:p-2.5">
          <div
            className="w-7 h-7 rounded-full grid place-items-center text-[11px] font-semibold text-white shrink-0"
            style={{
              background: 'linear-gradient(135deg, #6366f1 0%, #2563eb 100%)',
            }}
            aria-hidden="true"
          >
            {initials}
          </div>
          <div className="flex-1 min-w-0 md:hidden lg:block">
            <div className="text-[12px] font-semibold truncate">{me?.name ?? 'Loading…'}</div>
            <div className="text-[10px] uppercase tracking-wider text-side-text-faint truncate">
              {role?.replace('_', ' ') ?? ''}
            </div>
          </div>
          <button
            type="button"
            aria-label="Sign out"
            onClick={onLogout}
            className="w-7 h-7 grid place-items-center rounded-md text-side-text-muted hover:bg-side-hover hover:text-side-text transition-colors md:hidden lg:grid focus-visible:ring-2 focus-visible:ring-accent outline-none"
          >
            <Icon name="logout" size={14} />
          </button>
        </div>
      </div>
    </aside>
  );
}
